import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CSVLink } from 'react-csv';
import { Grid, IconButton, InputBase, makeStyles, Paper } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import SearchIcon from '@material-ui/icons/Search';
import KobooTablePagination from '../Layout/KobooTablePagination.jsx';
import UserRow from './UserRow';
import Loader from '../Layout/Loader';
import KobooButton from '../Layout/KobooButton';

const useStyles = makeStyles(() => (
  {
    formRoot: {
      padding: '2px 4px',
      display: 'flex',
      alignItems: 'center',
      width: 400,
    },
    input: {
      flex: 1,
    },
    iconButton: {
      padding: 10,
    },
  }
));
const columnData = [
  {
    id: 'lastname',
    numeric: false,
    disablePadding: false,
    label: 'Nom',
  },
  {
    id: 'firstname',
    numeric: false,
    disablePadding: false,
    label: 'Prénom',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email',
  },
  {
    id: 'phone',
    numeric: false,
    disablePadding: false,
    label: 'Téléphone',
  },
  {
    id: 'zipCode',
    numeric: false,
    disablePadding: false,
    label: 'Code Postal',
  },
  {
    id: '',
    numeric: false,
    disablePadding: false,
    label: '',
  },
];

const sort = (a, b, property, order) => {
  let prop1;
  let prop2;
  if (property === 'phone') {
    prop1 = a.mobile
      ? a.mobile.replace(' ', '').replace(':', '').replace('-', '').replace('/', '')
      : '';
    prop2 = b.mobile
      ? b.mobile.replace(' ', '').replace(':', '').replace('-', '').replace('/', '')
      : '';
  } else if (property === 'lastname' || property === 'firstname' || property === 'email') {
    prop1 = a[property]
      ? a[property].toUpperCase()
      : '';
    prop2 = b[property]
      ? b[property].toUpperCase()
      : '';
  } else {
    prop1 = (
      a[property] !== null && a[property] !== undefined
    )
      ? a[property]
      : -1;
    prop2 = (
      b[property] !== null && b[property] !== undefined
    )
      ? b[property]
      : -1;
  }
  if (order === 'asc') {
    return prop1 > prop2
      ? -1
      : 1;
  }
  return prop1 < prop2
    ? -1
    : 1;
};

const fieldNotEmptyAndContainsString = (field, string) => {
  return field
    !== undefined
    && field
    !== null
    && field
    !== ''
    && field.toUpperCase().indexOf(string.toUpperCase())
    !== -1;
};

export const UserTable = (props) => {
  const {
    users,
    authUser,
    pending,
  } = props;
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('lastname');
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [search, setSearch] = useState('');
  const csvLinkEl = useRef();

  useEffect(() => {
    if (users) {
      const usersArray = [];
      Object.keys(users).forEach((userId) => {
        const user = users[userId];
        user.id = userId;
        if (!search || search === '') {
          usersArray.push(user);
        } else {
          const searchLastname = fieldNotEmptyAndContainsString(user.lastname, search);
          const searchFirstname = fieldNotEmptyAndContainsString(user.firstname, search);
          const searchEmail = fieldNotEmptyAndContainsString(user.email, search);
          const searchPhone = fieldNotEmptyAndContainsString(user.phone, search);
          const searchZipCode = fieldNotEmptyAndContainsString(user.zipCode, search);
          if (searchLastname || searchFirstname || searchEmail || searchPhone || searchZipCode) {
            usersArray.push(user);
          }
        }
      });
      setData(
        usersArray.sort((a, b) => sort(a, b, orderBy, order)),
      );
    }
  }, [users, search]);

  const createSortHandler = property => {
    const newOrderBy = property;
    let newOrder = 'desc';
    if (orderBy === newOrderBy && order === newOrder) {
      newOrder = 'asc';
    }
    const newData = [...data];
    setData(newData.sort((a, b) => sort(a, b, newOrderBy, newOrder)));
    setOrder(newOrder);
    setOrderBy(newOrderBy);
  };

  const handleChangePage = (event, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
  };

  const dataForCSV = () => {
    let usersData = [];
    for (let user of data) {
      let csvUser = Object();
      csvUser['Nom'] = user.lastname;
      csvUser['Prenom'] = user.firstname;
      csvUser['Email'] = user.email;
      csvUser['Telephone'] = user.mobile;
      csvUser['Code Postal'] = user.zipCode;
      usersData.push(csvUser);
    }
    return usersData;
  };

  const onSubmitSearch = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSearch(event.target.search.value);
  };

  const clickDownloadCsv = (event) => {
    event.stopPropagation();
    csvLinkEl.current.link.click();
  };

  return (
    <React.Fragment>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <Paper component="form" className={classes.formRoot} onSubmit={onSubmitSearch}>
            <InputBase
              id="search"
              name="search"
              className={classes.input}
              placeholder="Rechercher"
              inputProps={{ 'aria-label': 'Rechercher' }}
            />
            <IconButton type="submit" className={classes.iconButton} aria-label="search">
              <SearchIcon />
            </IconButton>
          </Paper>
        </Grid>
        <Grid item>
          <KobooButton type="button" onClick={clickDownloadCsv}>
            Télécharger le CSV
          </KobooButton>
        </Grid>
      </Grid>
      <CSVLink
        ref={csvLinkEl}
        filename={`clients-${new Date().getTime()}.csv`}
        data={dataForCSV()}
        style={{ display: 'none' }}
      >-</CSVLink>
      <div>
        <Table>
          <TableHead>
            <TableRow>
              {columnData.map(column => (
                <TableCell
                  key={column.id}
                  padding={column.disablePadding
                    ? 'none'
                    : 'normal'}
                  sortDirection={orderBy === column.id
                    ? order
                    : false}>
                  <Tooltip
                    title="Trier"
                    placement={column.numeric
                      ? 'bottom-end'
                      : 'bottom-start'}
                    enterDelay={300}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={order}
                      onClick={() => createSortHandler(column.id)}>
                      {column.label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ), this)}
            </TableRow>
          </TableHead>
          <TableBody>
            {pending
              && <tr>
              <td colSpan={6}>
                <Loader />
              </td>
            </tr>
            }
            {!pending && Array.isArray(data) && data.length > 0
            && data.slice(page * rowsPerPage, (page * rowsPerPage) + rowsPerPage)
              .map(user => (
                <UserRow
                  key={user.id}
                  user={user}
                  authUser={authUser}
                />
              ))
            }
            {
              !pending
              && (Array.isArray(data) && data.length <= 0)
              && <tr>
                <td colSpan={6}>
                  <Typography style={{ textAlign: 'center' }} variant="subtitle1">
                    Aucun client{search !== '' && ' ne correspond à la recherche'}
                  </Typography>
                </td>
              </tr>

            }
          </TableBody>
        </Table>
      </div>
      <KobooTablePagination
        numberOfItems={data.length}
        rowsPerPage={rowsPerPage}
        label="Clients par page"
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
};

UserTable.propTypes = {
  authUser: PropTypes.object.isRequired,
  pending: PropTypes.bool.isRequired,
  users: PropTypes.object,
};

export default UserTable;



// WEBPACK FOOTER //
// ./src/components/Users/UserTable.jsx